import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, setAuthToken } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Students from "@/pages/Students";
import Alerts from "@/pages/Alerts";
import Tools from "@/pages/Tools";
import Vehicles from "@/pages/Vehicles";
import Settings from "@/pages/Settings";
import HistoryPage from "@/pages/HistoryPage";
import Vacations from "@/pages/Vacations";
import WorkerPortal from "@/pages/WorkerPortal";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-accent" />
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        setAuthToken(data.token);
        await checkAuth();
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return <FullLoader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "trabajador") return <Navigate to="/mi-ficha" replace />;
  return children;
}

function WorkerOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "trabajador") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/mi-ficha" element={<WorkerOnly><WorkerPortal /></WorkerOnly>} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="empleados" element={<Employees />} />
        <Route path="alumnos" element={<Students />} />
        <Route path="caducidades" element={<Alerts />} />
        <Route path="herramientas" element={<Tools />} />
        <Route path="vehiculos" element={<Vehicles />} />
        <Route path="vacaciones" element={<Vacations />} />
        <Route path="ajustes" element={<Settings />} />
        <Route path="historial" element={<HistoryPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
